// Platform detection and the native/web capability split.
//
// The SPA is ONE codebase serving a browser and a Capacitor shell. Everything
// that differs between them is decided here, so no view ever asks "am I in an
// app?" — it asks for a capability and gets whichever implementation exists.
//
// The Capacitor plugins are imported LAZILY, inside the functions that need them.
// That matters: a static import of `@capacitor/preferences` pulls its whole
// module graph into the web bundle for a code path the browser never takes. Lazy
// imports keep the web build the size it was.
//
// STATUS (2026-08-22, verified on the iPhone 17 Pro simulator against live
// production): `isNative()`/`platform()` and the secure token store are RUN AND
// WORKING — a cold start recovers the session from the platform keystore. Deep
// links remain natively unwired: `pathFromDeepLink()` is unit-tested, but
// `Info.plist` registers no `CFBundleURLTypes`, so no URL reaches the listener.
// See `web/CAPACITOR.md`.

/** True inside a Capacitor shell (iOS or Android), false in a browser. */
export function isNative(): boolean {
  // Capacitor injects this global. Checked defensively rather than importing
  // @capacitor/core at module scope, so the web bundle does not carry it.
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  try {
    return cap?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

export type Platform = 'web' | 'ios' | 'android';

export function platform(): Platform {
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  try {
    const p = cap?.getPlatform?.();
    if (p === 'ios' || p === 'android') return p;
  } catch {
    /* fall through */
  }
  return 'web';
}

// --- Persistent storage ---------------------------------------------------
//
// On the web this is localStorage. On native it is @capacitor/preferences, which
// is backed by the platform keystore — the migration plan is explicit that tokens
// must NOT sit in localStorage in the app.
//
// The Preferences API is ASYNC while localStorage is sync, and the token store's
// callers are all sync. Rather than make every call site async, the token store
// hydrates once at boot and writes through in the background. `loadSecure` is the
// hydrate; `saveSecure`/`deleteSecure` are the write-throughs.
//
// SCOPE: 'persistent' survives closing the browser or app; 'session' does not.
// Session scope is what "Stay logged in, unchecked" means, and on the web it is
// sessionStorage rather than nothing at all — a reload must NOT sign you out (in
// an SPA people press F5 constantly), but closing the tab must. That is exactly
// how a PHP session cookie behaved.
//
// On native there is no sessionStorage worth using and no browser to close, so
// session scope keeps the token in memory only: closing the app ends the session.

export type StorageScope = 'persistent' | 'session';

/** The web store for a scope. Throws in private mode, so callers guard. */
function webStore(scope: StorageScope): Storage {
  return scope === 'session' ? sessionStorage : localStorage;
}

export async function loadSecure(
  key: string,
  scope: StorageScope = 'persistent',
): Promise<string | null> {
  if (!isNative()) {
    try {
      return webStore(scope).getItem(key);
    } catch {
      return null;
    }
  }
  // Native session scope is memory-only by design, so there is nothing to load.
  if (scope === 'session') return null;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    return value ?? null;
  } catch (err) {
    console.error(`loadSecure(${key}) failed:`, err);
    return null;
  }
}

export async function saveSecure(
  key: string,
  value: string,
  scope: StorageScope = 'persistent',
): Promise<void> {
  if (!isNative()) {
    try {
      webStore(scope).setItem(key, value);
    } catch {
      /* private mode */
    }
    return;
  }
  // Memory-only on native for session scope: the in-memory mirror in tokens.ts
  // already holds it, so writing nothing here is what ends the session on close.
  if (scope === 'session') return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key, value });
  } catch (err) {
    console.error(`saveSecure(${key}) failed:`, err);
  }
}

/**
 * Remove the key from BOTH web scopes.
 *
 * Deliberately not scope-aware: logout must not leave a copy behind in the store
 * it did not happen to be using. Clearing both is cheap and cannot be wrong.
 */
export async function deleteSecure(key: string): Promise<void> {
  if (!isNative()) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key });
  } catch (err) {
    console.error(`deleteSecure(${key}) failed:`, err);
  }
}

// --- Photo capture ---------------------------------------------------------
//
// DELETED 2026-08-22: `pickPhoto()` and its `PickedPhoto` type lived here and
// wrapped @capacitor/camera. Nothing ever called them.
//
// `views/photos.ts` renders an ordinary `<input type="file">` and never consults
// `isNative()`, and that input already raises the native Photo Library / Take
// Photo / Choose File sheet inside the shell — verified on device 2026-08-22,
// including an upload that reached the CDN. So the native wrapper added a second
// way to do something the WebView already did natively, and could not be
// exercised or regression-tested from any view.
//
// Removed rather than wired in, for the reason the APNs sender is deliberately
// unbuilt: native code no view can reach cannot be verified, and rots while
// reading as though it works. If a native picker is ever genuinely wanted (for
// multi-select, or to skip the sheet), write it then, against a view that calls
// it.
//
// ⚠ The camera/photo-library usage strings in `Info.plist` MUST STAY. They are
// not for the deleted plugin — the file input's "Take Photo" goes through the
// camera, and a missing usage string is an immediate crash, not a prompt.

// --- Deep links -----------------------------------------------------------

/**
 * Route incoming deep links through the SPA router.
 *
 * A tapped link like `mysocialcal://confirm?t=…` or a universal link to
 * `https://mysocialcal.com/events/42` must land on the right view WITHOUT a page
 * load — the shell has no server to ask, and a reload would drop the WebSocket.
 *
 * Only the path and query are taken from the incoming URL. The origin is
 * discarded deliberately: honouring it would let a crafted link navigate the app
 * somewhere else entirely.
 */
export async function startDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!isNative()) return;

  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('appUrlOpen', ({ url }) => {
      const path = pathFromDeepLink(url);
      if (path) navigate(path);
    });
  } catch (err) {
    console.error('startDeepLinks failed:', err);
  }
}

/**
 * The in-app path for a deep link, or null when it is not one we handle.
 *
 * Exported for its own test: this is string handling on untrusted input, and the
 * origin-discarding above is a security property rather than a convenience.
 */
export function pathFromDeepLink(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // ⚠ A CUSTOM SCHEME PUTS THE FIRST SEGMENT IN `host`, NOT `pathname`.
  // `new URL('mysocialcal://events/42')` gives host='events', pathname='/42', so
  // reading pathname alone navigates to '/42' and loses the segment that decides
  // which view opens. Only http(s) are "special" schemes where the host is
  // genuinely a host. This cost a test failure to notice.
  const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';
  const raw = isHttp
    ? `${parsed.pathname}${parsed.search}`
    : `/${parsed.host}${parsed.pathname}${parsed.search}`;

  // Collapse leading slashes. A path beginning `//` is PROTOCOL-RELATIVE if it
  // ever reaches an href, so `mysocialcal:////evil.example.com/x` must not come
  // back as something that could resolve off-origin.
  const path = raw.replace(/^\/+/, '/');
  // A bare scheme open (`mysocialcal://`) has no useful path — stay put rather
  // than navigating somewhere arbitrary.
  if (path === '' || path === '/') return null;
  return path;
}

// --- Push registration (native) -------------------------------------------

/**
 * Register for APNs / FCM and hand the device token to the server.
 *
 * ⚠ THE SENDER IS NOT BUILT. This registers the device and stores its token so
 * nothing is blocked, but the server has no APNs sender — that was deferred by
 * agreement to where it can be tested against a real device, which is not a
 * Windows machine. `push_subscriptions.platform` distinguishes these rows from
 * Web Push ones so a future sender can select them.
 *
 * On the web this does nothing: Web Push is handled by `web/src/push.ts`, which
 * has its own VAPID flow.
 */
export async function registerNativePush(
  onToken: (token: string, platform: Platform) => Promise<void>,
): Promise<string | null> {
  if (!isNative()) return 'Native push is only available in the app.';

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return 'Notification permission was not granted.';

    await PushNotifications.addListener('registration', (token) => {
      void onToken(token.value, platform());
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('native push registration failed:', err);
    });

    await PushNotifications.register();
    return null;
  } catch (err) {
    return `Could not register for notifications: ${String(err)}`;
  }
}
